import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import { Calendar, Clock, ArrowRight, Tag, FileText } from "lucide-react";
import { Link } from "react-router-dom";

interface BlogPost {
  id: string;
  title: string;
  description: string;
  content: string;
  date: string;
  slug: string;
  category: string;
  readTime: string;
  featured?: boolean;
}

const blogPosts: BlogPost[] = [
  {
    id: "1",
    title: "How Premium Businesses Scale with Targeted SEO",
    description: "Discover the proven strategies that help premium businesses attract high-value clients through intelligent SEO positioning.",
    content: "Our approach to SEO for premium businesses focuses on quality over quantity. We help you rank for terms that attract your ideal clients, not just traffic. Learn how strategic keyword targeting, technical optimization, and content authority building combine to create sustainable organic growth.",
    date: "2025-01-20",
    slug: "premium-seo-strategies",
    category: "SEO",
    readTime: "8 min read",
    featured: true
  },
  {
    id: "2",
    title: "The ROI of Professional Digital Marketing",
    description: "Real numbers from real businesses: See how strategic digital marketing delivers measurable revenue growth.",
    content: "On average, our clients see 150% revenue increases within the first year. Here's how we achieve these results through data-driven strategies, continuous optimization, and focusing on metrics that actually matter to your bottom line.",
    date: "2025-01-15",
    slug: "digital-marketing-roi",
    category: "Marketing Strategy",
    readTime: "6 min read"
  },
  {
    id: "3",
    title: "Why Your Website Isn't Converting Visitors",
    description: "The hidden conversion killers that are costing you high-ticket clients and how to fix them.",
    content: "Most premium business websites make critical mistakes that repel ideal clients. Learn the specific elements that build trust and drive conversions, from social proof placement to call-to-action optimization.",
    date: "2025-01-10",
    slug: "website-conversion-optimization",
    category: "Conversion",
    readTime: "10 min read"
  },
  {
    id: "4",
    title: "B2B Lead Generation: Quality Over Quantity",
    description: "Why chasing volume kills your sales team and how to build a pipeline of qualified prospects.",
    content: "The most successful B2B companies we work with don't focus on lead volume—they focus on lead quality. Discover the frameworks and systems that attract decision-makers ready to buy.",
    date: "2025-01-05",
    slug: "b2b-lead-quality",
    category: "Lead Generation",
    readTime: "7 min read"
  },
  {
    id: "5",
    title: "LinkedIn Ads for Enterprise Sales",
    description: "A complete guide to using LinkedIn advertising to reach enterprise decision-makers.",
    content: "LinkedIn remains the most effective platform for B2B advertising, but most campaigns fail due to poor targeting and weak creative. Learn our proven framework for enterprise LinkedIn campaigns.",
    date: "2024-12-28",
    slug: "linkedin-enterprise-ads",
    category: "Paid Advertising",
    readTime: "12 min read"
  }
];

const categories = ["All", "SEO", "Marketing Strategy", "Conversion", "Lead Generation", "Paid Advertising"];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredPosts = activeCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const featuredPost = blogPosts.find(post => post.featured);

  return (
    <>
      <EnhancedSEO
        type="resources"
        title="B2B Marketing Insights & Strategies | VGP Blog"
        description="Expert insights on SEO, lead generation, and digital growth strategies for B2B businesses. Learn proven tactics to scale your business."
        keywords="B2B marketing blog, SEO strategies, lead generation tips, business growth, digital marketing insights"
        url="/blog"
      />
      
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-background via-secondary/5 to-accent/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-4">VGP Insights</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Marketing Intelligence
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Actionable strategies and insights for B2B growth. No fluff, just results-driven content.
              </p>
              
              {/* White Papers CTA */}
              <div className="flex justify-center">
                <Button asChild variant="outline" className="group">
                  <Link to="/white-papers">
                    <FileText className="w-4 h-4 mr-2" />
                    Download Free White Papers
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && (
          <section className="py-12 border-b border-border/50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <Badge className="mb-6 bg-accent text-primary">Featured</Badge>
                <Card className="border-accent/20 bg-gradient-to-br from-secondary/5 to-accent/5">
                  <CardHeader className="pb-4">
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        <span>{featuredPost.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <time dateTime={featuredPost.date}>
                          {new Date(featuredPost.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </time>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{featuredPost.readTime}</span>
                      </div>
                    </div>
                    <CardTitle className="text-3xl md:text-4xl text-foreground">{featuredPost.title}</CardTitle>
                    <CardDescription className="text-lg">{featuredPost.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground leading-relaxed mb-6">{featuredPost.content}</p>
                    <Button className="group">
                      Read Full Article
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        )}

        {/* Category Filter */}
        <section className="py-8 border-b border-border/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={activeCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.filter(p => !p.featured).map((post) => (
                  <Card key={post.id} className="hover:shadow-lg transition-all duration-300 border-border/50 hover:border-accent/30 flex flex-col">
                    <CardHeader className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">{post.category}</Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-xl text-foreground leading-tight">{post.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{post.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <time dateTime={post.date} className="text-sm text-muted-foreground">
                          {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </time>
                        <Button variant="ghost" size="sm" className="group">
                          Read
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">
                Get Weekly Growth Insights
              </h2>
              <p className="text-white/90 mb-6">
                Join 2,000+ B2B marketers receiving actionable strategies every Thursday.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" variant="secondary" className="group">
                  <Link to="/book-call">
                    Subscribe to Newsletter
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
